import { CheckBadgeIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { INVALID_ALL_INPUTS } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getInvestorServicers, getInvestorServicersItem } from 'services/apis/conditions'
import { filterParticipants } from 'services/apis/participants'
import { Modal, Tooltip } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { participationInputs } from './constant'
import type { IParticipant } from './types'

interface InvestorServicer {
  id: number
  investorCode: string
  fullName: string
  type: number
  data?: Record<string, any>
}

const buildRepName = (data: Record<string, any> = {}) => {
  return [data.accRep1FirstName, data.accRep1MiddleName, data.accRep1LastName].filter(Boolean).join(' ').trim()
}

export const AddParticipation = ({
  index,
  item,
  interestRate,
  onClose,
  onUpdate,
  loading,
}: {
  index: number
  item: IParticipant | null
  interestRate: string
  onClose: Function
  onUpdate: Function
  loading: boolean
}) => {
  const [inputs, setInputs] = useState(participationInputs())
  const [investorServicers, setInvestorServicers] = useState<InvestorServicer[]>([])
  const [fetchingInvestor, setFetchingInvestor] = useState(false)
  // Global participant (participant-user) this loan participation is linked to. Payment
  // distributions are only logged when this is set, so it is exposed here so an unlinked
  // participant can be linked without a manual DB fix.
  const [participantUserId, setParticipantUserId] = useState('')
  const [participantOptions, setParticipantOptions] = useState<Record<string, string>>({})

  useEffect(() => {
    getInvestorServicers().then((data: InvestorServicer[]) => {
      setInvestorServicers(data)

      const optionsRecord: Record<string, string> = {}
      data.forEach((inv) => {
        optionsRecord[inv.id.toString()] = `${inv.investorCode} - ${inv.fullName}`
      })

      const newInputs = cloneDeep(participationInputs())
      newInputs.participantName.inputType = 'SearchableSelect'
      newInputs.participantName.options = optionsRecord

      // The participant balance is managed through the Add Participation wizard (allocations add to
      // the existing amount); it must not be edited directly here.
      newInputs.participantAmount.disabled = true
      newInputs.participantAmount.title = 'Investment Amount (managed via Add Funding)'

      if (item) {
        Object.keys(newInputs).forEach((key) => {
          if ((item as any)[key] !== undefined) {
            newInputs[key].value = (item as any)[key]
          }
        })

        const matched = data.find(
          (inv) =>
            inv.fullName === item.participantName ||
            `${inv.investorCode} - ${inv.fullName}` === item.participantName,
        )
        if (matched) {
          newInputs.participantName.value = matched.id.toString()
        } else if (item.participantName) {
          optionsRecord[`existing`] = item.participantName
          newInputs.participantName.options = optionsRecord
          newInputs.participantName.value = 'existing'
        }
      }

      setInputs(newInputs)
    })
  }, [item])

  useEffect(() => {
    setParticipantUserId(item?.participantUserId ?? '')
    // The global participants directory — the same source the Add Participation wizard links new
    // participants from, so an existing/seeded participant can be attached to one here.
    filterParticipants({ query: '', count: 1000, skip: 0, orderBy: 'fullName', orderDir: 1 })
      .then((res: any) => {
        const rec: Record<string, string> = {}
        ;(Array.isArray(res?.data) ? res.data : []).forEach((pt: any) => {
          rec[String(pt.id)] = pt.email ? `${pt.fullName} (${pt.email})` : pt.fullName
        })
        setParticipantOptions(rec)
      })
      .catch(() => {})
  }, [item])

  const fillFromInvestor = async (investorId: string, currentInputs: Record<string, any>) => {
    const selectedInvestor = investorServicers.find((inv) => inv.id.toString() === investorId)
    if (!selectedInvestor) return currentInputs

    setFetchingInvestor(true)
    try {
      const detail: InvestorServicer = await getInvestorServicersItem(selectedInvestor.id)
      const detailData = detail?.data || {}
      const next = cloneDeep(currentInputs)

      next.participantName.value = investorId
      next.participantName.error = InputValidate({ ...next.participantName, value: investorId })

      const repName = buildRepName(detailData) || detailData.shippingContact || detailData.shippingName || ''
      if (repName) {
        next.participantRepName.value = repName
        next.participantRepName.error = InputValidate({ ...next.participantRepName, value: repName })
      }

      if (detailData.accRep1Title) {
        next.participantRepTitle.value = detailData.accRep1Title
        next.participantRepTitle.error = InputValidate({
          ...next.participantRepTitle,
          value: detailData.accRep1Title,
        })
      }

      const address = detailData.businessAddress || detailData.shippingAddress || detailData.lossPayeeAddress || ''
      if (address) {
        next.participantAddress.value = address
        next.participantAddress.error = InputValidate({ ...next.participantAddress, value: address })
      }

      return next
    } finally {
      setFetchingInvestor(false)
    }
  }

  const onChange = async (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value

    if (key === 'participantName' && value && value !== 'existing') {
      newInputs = await fillFromInvestor(value, newInputs)
    }

    setInputs(newInputs)
  }

  const fillDefaultValues = () => {
    const temp = cloneDeep(inputs)
    const rate = Number(interestRate)
    temp.principalRate.value = rate
    temp.principalBuyoutRate.value = rate
    temp.participantExcessReturnPercent.value = 0
    temp.participantExtensionPercent.value = 0
    temp.participantOriginationFeePercent.value = 0
    temp.participantServicingFeePercent.value = 0
    Object.keys(temp).forEach((key) => {
      temp[key].error = InputValidate({ ...temp[key] })
    })
    setInputs(temp)
  }

  const defaultFragment = useMemo(() => {
    return `
      <div>
        <div>Principal Rate: ${interestRate}</div>
        <div>Principal Buyout Rate: ${interestRate}</div>
        <div>Investor Excess Return Percent: 0</div>
        <div>Investor Extension Percent: 0</div>
        <div>Investor Origination Fee Percent: 0</div>
      </div>
    `
  }, [interestRate])

  const resolveParticipantName = (value: string) => {
    if (!value) return ''
    if (value === 'existing') return item?.participantName || ''
    const selected = investorServicers.find((inv) => inv.id.toString() === value)
    return selected?.fullName || value
  }

  const onSubmit = async () => {
    let hasError = false
    const newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = `${newInputs[key].value}`
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) {
      toast(INVALID_ALL_INPUTS, { type: 'error' })
      return
    }

    data.participantName = resolveParticipantName(data.participantName)
    // Carry the global-participant link through the update (the backend replaces the whole info
    // blob, so leaving it out would wipe it and silently stop payment distributions).
    data.participantUserId = participantUserId
    onUpdate(data)
  }

  return (
    <Modal
      title={item ? `Update Investor - No.${index + 1}` : 'Add Investor'}
      titleOkay="Submit"
      loading={loading || fetchingInvestor}
      onClose={() => onClose()}
      onOk={onSubmit}
      childLevel={2}
      isOpen
    >
      <div className="text-gray-900">
        <div className="flex flex-wrap gap-4 mb-4 items-center">
          <div>
            <RenderInput
              input={{
                inputType: 'text',
                title: 'Loan Interest Rate',
                value: interestRate,
                prefix: '%',
                readOnly: true,
              }}
              Key="InterestRate"
              onChange={() => {}}
            />
          </div>
          <div
            className="flex gap-1 h-fit hover:underline cursor-pointer text-shade-blue items-center text-[14px]"
            onClick={fillDefaultValues}
          >
            <Tooltip message={defaultFragment}>
              <span>Fill Default Values</span>
            </Tooltip>
            <CheckBadgeIcon className="w-4 h-4" />
          </div>
        </div>
        <div className="mb-4 max-w-md">
          <RenderInput
            input={{
              inputType: 'select',
              title: 'Linked Investor (required for payment distributions)',
              value: participantUserId,
              options: participantOptions,
              hasDefaultOption: true,
              defaultOptionText: '- Not linked -',
            }}
            Key="participantUserId"
            onChange={(_k: string, v: any) => setParticipantUserId(v)}
          />
        </div>
        <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.keys(inputs).map((key) => (
            <div key={key} className={`md:col-span-${inputs[key].span || 1}`}>
              <RenderInput input={inputs[key]} Key={key} onChange={onChange} />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}
